import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { fetchDiaryPolicy, updateDiaryPolicy, type DiaryPolicy } from '@/lib/diaryPolicyApi';
import { getApiErrorMessage } from '@/lib/api';

// number[] <-> comma-separated string for the reminder inputs
const toStr = (a: number[]) => a.join(', ');
const toArr = (s: string) =>
  s.split(',').map((x) => Number(x.trim())).filter((n) => Number.isFinite(n) && n >= 0);

export default function DiaryPolicyPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['diary-policy'], queryFn: fetchDiaryPolicy });
  const [form, setForm] = useState<DiaryPolicy | null>(null);
  const [eventStr, setEventStr] = useState('');
  const [hearingStr, setHearingStr] = useState('');

  if (q.data && form === null) {
    setForm(q.data);
    setEventStr(toStr(q.data.eventReminderMinutes));
    setHearingStr(toStr(q.data.hearingReminderMinutes));
  }

  const mut = useMutation({
    mutationFn: (input: Partial<DiaryPolicy>) => updateDiaryPolicy(input),
    onSuccess: (d) => { toast.success('Diary policy saved'); setForm(d); qc.invalidateQueries({ queryKey: ['diary-policy'] }); },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not save')),
  });

  const save = () => {
    if (!form) return;
    mut.mutate({
      eventReminderMinutes: toArr(eventStr),
      hearingReminderMinutes: toArr(hearingStr),
      travelBufferMinutes: form.travelBufferMinutes,
      doubleBookingBlocked: form.doubleBookingBlocked,
    });
  };

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title="Diary Policy"
        subtitle="Chambers-wide diary defaults. These feed the calendar engine — reminder timings, travel buffer, and booking rules."
      />

      {q.isLoading || !form ? (
        <div className="py-16 text-center text-sm text-muted-foreground">Loading…</div>
      ) : (
        // Full width, like the card on every other page. A 36rem card left half the screen
        // empty whether it sat left (void on the right) or centred (a void each side); the
        // fields below use the width instead, so there is nothing to leave empty.
        <div className="space-y-3">
          <Card>
            <CardContent className="grid gap-x-5 gap-y-3.5 p-4 sm:grid-cols-2 lg:grid-cols-3">
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-muted-foreground">Event reminders (minutes before)</span>
                <input value={eventStr} onChange={(e) => setEventStr(e.target.value)} placeholder="30" className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-400" />
                <p className="mt-1 text-xs text-muted-foreground">Comma-separated. Applied to new diary events (e.g. “30” or “60, 15”).</p>
              </label>

              <label className="block">
                <span className="mb-1 block text-xs font-medium text-muted-foreground">Hearing reminders (minutes before)</span>
                <input value={hearingStr} onChange={(e) => setHearingStr(e.target.value)} placeholder="1440, 60" className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-400" />
                <p className="mt-1 text-xs text-muted-foreground">e.g. “1440, 60” = a day before and an hour before.</p>
              </label>

              <label className="block">
                <span className="mb-1 block text-xs font-medium text-muted-foreground">Travel buffer (minutes)</span>
                <input type="number" min={0} max={480} value={form.travelBufferMinutes} onChange={(e) => setForm({ ...form, travelBufferMinutes: Number(e.target.value) || 0 })} className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-400" />
                <p className="mt-1 text-xs text-muted-foreground">Time to reserve around hearings for travel.</p>
              </label>

              <label className="flex items-start gap-2 sm:col-span-2 lg:col-span-3">
                <input type="checkbox" checked={form.doubleBookingBlocked} onChange={(e) => setForm({ ...form, doubleBookingBlocked: e.target.checked })} className="mt-0.5 h-4 w-4 rounded border-border text-brand-600 dark:text-brand-400 focus:ring-brand-400" />
                <span>
                  <span className="text-sm text-foreground">Block double-booking</span>
                  <span className="block text-xs text-muted-foreground">Warn/prevent overlapping bookings for the same barrister.</span>
                </span>
              </label>
            </CardContent>
          </Card>
          <div className="flex justify-end">
            <Button variant="primary" size="md" onClick={save} disabled={mut.isPending}>{mut.isPending ? 'Saving…' : 'Save policy'}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
